import { chunk } from "lodash";

import { CategoryCustomFieldOrder } from "../models/categories";
import {
  CustomField,
  CustomFieldDTO,
  CustomFieldUpdate,
  CustomSelectFieldValue,
  NewCustomField,
  serializeCustomField,
} from "../models/customFields";
import {
  AssetsPaginatedResponse,
  CursorState,
  DistributivePick,
  ProjectId,
} from "../types";
import {
  CATEGORY_CUSTOM_FIELD_ORDER_BATCH_LIMIT,
  CUSTOM_ATTRIBUTES_BATCH_FETCH_MAX,
  Endpoints,
} from "./constants";
import { AssetsHttpClient } from "./createAssetsHttpClient";
import { buildSearchString } from "./helpers";

export const getCustomFieldClient = (
  http: AssetsHttpClient,
  projectId: ProjectId
) => ({
  batchGetCustomFieldOrders: async (
    categoryIds: string[]
  ): Promise<CategoryCustomFieldOrder[]> => {
    const generateRequest = (batchIds: string[]) =>
      http.post<{ results: CategoryCustomFieldOrder[] }>(
        `${http.getAssetsServiceBasePath(projectId)}/${
          Endpoints.CategoryCustomAttributes
        }/order:batch-get`,
        { body: JSON.stringify({ ids: batchIds }) }
      );

    if (categoryIds.length > CATEGORY_CUSTOM_FIELD_ORDER_BATCH_LIMIT) {
      const results = [];
      const idChunks = chunk(
        categoryIds,
        CATEGORY_CUSTOM_FIELD_ORDER_BATCH_LIMIT
      );

      for (const batchIds of idChunks) {
        results.push(...(await generateRequest(batchIds)).results);
      }
      return results;
    }

    return (await generateRequest(categoryIds)).results;
  },

  list: async (
    cursorState?: CursorState,
    { includeDeleted }: { includeDeleted?: boolean } = {}
  ): Promise<AssetsPaginatedResponse<CustomFieldDTO[]>> => {
    const searchString = buildSearchString({ cursorState, includeDeleted });

    return http.get<AssetsPaginatedResponse<CustomFieldDTO[]>>(
      `${http.getAssetsServiceBasePath(projectId)}/${
        Endpoints.CustomAttributes
      }${searchString ? `?${searchString}` : ""}`
    );
  },

  getByCategoryId: async (
    categoryId: string,
    includeInherited: boolean = true
  ): Promise<AssetsPaginatedResponse<CustomFieldDTO[]>> => {
    const searchString = buildSearchString({ includeInherited });

    return http.get<AssetsPaginatedResponse<CustomFieldDTO[]>>(
      `${http.getAssetsServiceBasePath(projectId)}/${
        Endpoints.Categories
      }/${categoryId}/${Endpoints.CustomAttributes}?${searchString}`
    );
  },

  batchGetById: async (ids: CustomField["id"][]): Promise<CustomFieldDTO[]> => {
    const generateRequest = (batchIds: CustomField["id"][]) =>
      http.post<{ results: CustomFieldDTO[] }>(
        `${http.getAssetsServiceBasePath(projectId)}/${
          Endpoints.CustomAttributes
        }:batch-get`,
        { body: JSON.stringify({ ids: batchIds }) }
      );

    if (ids.length > CUSTOM_ATTRIBUTES_BATCH_FETCH_MAX) {
      const results = [];
      const idChunks = chunk(ids, CUSTOM_ATTRIBUTES_BATCH_FETCH_MAX);

      for (const batchIds of idChunks) {
        results.push(...(await generateRequest(batchIds)).results);
      }
      return results;
    }

    return (await generateRequest(ids)).results;
  },

  batchGetByName: async (
    names: CustomField["name"][]
  ): Promise<CustomFieldDTO[]> => {
    const generateRequest = (batchNames: CustomField["name"][]) =>
      http.post<{ results: CustomFieldDTO[] }>(
        `${http.getAssetsServiceBasePath(projectId)}/${
          Endpoints.CustomAttributes
        }:batch-get`,
        { body: JSON.stringify({ names: batchNames }) }
      );

    if (names.length > CUSTOM_ATTRIBUTES_BATCH_FETCH_MAX) {
      const results = [];
      const nameChunks = chunk(names, CUSTOM_ATTRIBUTES_BATCH_FETCH_MAX);

      for (const batchNames of nameChunks) {
        results.push(...(await generateRequest(batchNames)).results);
      }
      return results;
    }

    return (await generateRequest(names)).results;
  },

  get: async (id: CustomField["id"]): Promise<CustomFieldDTO> =>
    http.get<CustomFieldDTO>(
      `${http.getAssetsServiceBasePath(projectId)}/${
        Endpoints.CustomAttributes
      }/${id}`
    ),

  create: async (
    field: NewCustomField & Pick<CustomField, "requiredOnIngress">,
    renameConflicting?: boolean
  ): Promise<CustomFieldDTO> => {
    const searchString = buildSearchString({ renameConflicting });

    return http.post<CustomFieldDTO>(
      `${http.getAssetsServiceBasePath(projectId)}/${
        Endpoints.CustomAttributes
      }${searchString ? `?${searchString}` : ""}`,
      { body: JSON.stringify(serializeCustomField(field)) }
    );
  },

  update: async ({
    id,
    ...field
  }: CustomFieldUpdate &
    DistributivePick<CustomField, "id" | "dataType">): Promise<CustomFieldDTO> => {
    const { dataType, ...serializedField } = serializeCustomField(field);

    return http.patch<CustomFieldDTO>(
      `${http.getAssetsServiceBasePath(projectId)}/${
        Endpoints.CustomAttributes
      }/${id}`,
      { body: JSON.stringify(serializedField) }
    );
  },

  updateEnumValue: async (
    id: CustomSelectFieldValue["id"],
    newDisplayName: CustomSelectFieldValue["displayName"]
  ): Promise<CustomFieldDTO> =>
    http.patch<CustomFieldDTO>(
      `${http.getAssetsServiceBasePath(projectId)}/${
        Endpoints.CustomAttributeSelectValues
      }:batch-patch?renameConflicting=true`,
      { body: JSON.stringify({ [id]: { displayName: newDisplayName } }) }
    ),

  delete: async ({
    id,
  }: DistributivePick<CustomField, "id">): Promise<CustomFieldDTO> =>
    http.del<CustomFieldDTO>(
      `${http.getAssetsServiceBasePath(projectId)}/${
        Endpoints.CustomAttributes
      }/${id}`
    ),

  undelete: async (id: CustomField["id"]): Promise<CustomFieldDTO> =>
    http.post<CustomFieldDTO>(
      `${http.getAssetsServiceBasePath(projectId)}/${
        Endpoints.CustomAttributes
      }/${id}/undelete`
    ),
});
